import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfigService } from './services/config.service';
import { SharedDataService } from './services/shared-data.service';
import { AccountInfoService } from './services/account-info.service';
import { SSOService } from './services/sso.service';
import { LoadingService } from './services/loading.service';
import { AuthService } from './services/auth.service';
import { Config, Plan, Integration } from './models/config.model';
import { NavigationService } from './services/navigation.service';
import { slideInAnimation } from './animations';
import { LocalStorageService } from './services/local-storage.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [slideInAnimation]
})
export class AppComponent implements OnInit {
  config: Config;
  initialized: boolean = false;
  isLoading$ = this.loadingService.loading$;

  constructor(
    private configService: ConfigService,
    private loadingService: LoadingService,
    private sharedDataService: SharedDataService,
    private authService: AuthService,
    private accountInfoService: AccountInfoService,
    private ssoService: SSOService,
    public navigationService: NavigationService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    // Show the loading indicator.
    this.loadingService.show();

    // Determine if the URL indicates a successful purchase.
    const queryParams = new URLSearchParams(window.location.search);
    const isSuccess = queryParams.has('success');

    // Set environment into shared data.
    this.sharedDataService.setEnv(this.getEnvironment());

    const authkey = this.authService.checkAuthkeyCookie();

    if (authkey) {
      // Existing user flow.
      this.authService.setExistingCustomer(true);
      this.localStorageService.setItem('authToken', authkey);
      forkJoin({
        config: this.configService.getData(),
        accountInfo: this.accountInfoService.getAccountInfo(authkey)
      }).subscribe({
        next: ({ config, accountInfo }) => {
          this.sharedDataService.setConfig(config);
          this.config = config;

          if (accountInfo) {
            this.setAccountInfo(accountInfo);

            // Get the return URL through SSO.
            const accountReturnURL = accountInfo?.creditCardInfoStripe?.returnUrl;
            this.ssoService.getSSOLink(authkey, accountReturnURL)
              .pipe(
                catchError(error => {
                  console.error('Error fetching SSO link:', error);
                  this.loadingService.hide();
                  return of({ result: '' });
                })
              )
              .subscribe({
                next: (response) => {
                  this.sharedDataService.setReturnURL(response.result);
                  this.localStorageService.setItem('returnURL', response.result);
                  // Initialize navigation for existing accounts.
                  this.navigationService.initializeNavigation(isSuccess, true);
                  this.loadingService.hide();
                  this.initialized = true;
                },
                error: () => {
                  this.loadingService.hide();
                }
              });
          } else {
            // No account info fallback.
            this.navigationService.initializeNavigation(isSuccess, true);
            this.loadingService.hide();
            this.initialized = true;
          }
        },
        error: (error) => {
          console.error('Error loading data for existing user:', error);
          this.loadingService.hide();
        }
      });
    } else {
      // New user flow.
      this.configService.getData().subscribe({
        next: (config) => {
          this.sharedDataService.setConfig(config);
          this.config = config;
          // SFX-25902: honor direct-entry URL params from the public website.
          const directEntry = this.applyDirectEntryParams(queryParams, config);
          // Initialize navigation for new accounts.
          this.navigationService.initializeNavigation(isSuccess, false, directEntry);
          this.loadingService.hide();
          this.initialized = true;
        },
        error: (error) => {
          console.error('Error loading config:', error);
          this.loadingService.hide();
        }
      });
    }
  }

  // SFX-25902: parse ?plan=&term=&integration= from the public-website entry URL
  // and hydrate SharedDataService so the user lands on checkout pre-populated.
  // Returns true when a valid plan was resolved (caller should skip the selection page).
  private applyDirectEntryParams(queryParams: URLSearchParams, config: Config): boolean {
    const planSlug = queryParams.get('plan')?.trim().toLowerCase();
    if (!planSlug) {
      return false;
    }
    const plan = this.resolvePlanBySlug(planSlug, config.plans);
    if (!plan) {
      return false;
    }
    this.sharedDataService.setPlan(plan);
    this.sharedDataService.setDirectEntry(true);
    // 700/yr cap per SFX-25902 — backend will enforce; first priceRange covers 1-700.
    this.sharedDataService.setRangeIndex(0);

    const term = queryParams.get('term')?.trim().toLowerCase();
    if (term === 'monthly' || term === 'annual') {
      this.sharedDataService.setBillingTerm(term);
    }

    const integrationSlug = queryParams.get('integration')?.trim().toLowerCase();
    if (integrationSlug) {
      const integration = this.resolveIntegrationBySlug(integrationSlug, config.integrations);
      if (integration) {
        this.sharedDataService.setIntegrationEnabled(true);
        this.sharedDataService.setSelectedIntegration(integration);
      }
    }
    return true;
  }

  private resolvePlanBySlug(slug: string, plans: Plan[]): Plan | null {
    // Public-website slugs may differ from backend planName; map then match by title.
    const slugToTitle: { [key: string]: string } = {
      basic: 'basic',
      pro: 'professional',
      professional: 'professional'
    };
    const target = slugToTitle[slug];
    if (!target) {
      return null;
    }
    return plans.find(p => p.title.toLowerCase() === target) || null;
  }

  private resolveIntegrationBySlug(slug: string, integrations: Integration[]): Integration | null {
    return integrations.find(i => i.name.toLowerCase() === slug) || null;
  }

  getEnvironment(): string {
    const hostname = window.location.hostname;
    if (hostname.includes('gamma')) {
      return 'gamma';
    } else if (hostname.includes('webtest')) {
      return 'webtest';
    } else {
      return 'dev';
    }
  }

  setAccountInfo(accountInfo: any): void {
    this.sharedDataService.setAccountInfo(accountInfo);
    this.sharedDataService.setCreditCardInfoStripe({ ...accountInfo.creditCardInfoStripe });
  }
}
